import React from 'react';
import {
    ShoppingCart,
    CalendarMonth,
    Receipt,
    Print,
    Business,
    AdminPanelSettings,
    Settings,
    Security,
    Domain,
    Store,
    Lock,
    Badge,
    Person,
    Tune,
    LibraryBooks,
    Extension,
    ViewQuilt,
    LocalGasStation,
    CreditCard,
    Speed,
    TrendingUp,
    PieChart,
    Message,
    CloudUpload,
    Quiz,
    Help,
    Assessment,
    Link,
    ListAlt,
    LocalOffer,
    DashboardCustomize,
    Traffic,
    Drafts,
    Email,
    // boubaker 23/08/2026 - menu Impressions Academy
    Description as DescriptionIcon,
    AccountBalance as AccountBalanceIcon,
    Dashboard,
    Assignment,
    Explore,
    SupportAgent,
    GridView,
    FactCheck,
    School,
    Groups,
    ContactPage,
    TransferWithinAStation,
    Verified,
    Mail,
    LocalHospital,
    TableChart,
    CardMembership,
    WorkspacePremium,
    AutoAwesome,
    DeleteSweep
} from '@mui/icons-material';

export interface MenuItem {
    caption: string;
    translationKey?: string;
    type: 'header' | 'link' | 'link-parent';
    classColor?: string;
    href?: string;
    icon?: React.ReactNode;
    bmsyu_objet?: string; // Legacy property kept
    child?: MenuItem[];
    for_admin?: boolean;
}

export const iconMap: { [key: string]: React.ReactNode } = {
    ShoppingCart: <ShoppingCart />,
    CalendarMonth: <CalendarMonth />,
    Receipt: <Receipt />,
    Print: <Print />,
    Business: <Business />,
    AdminPanelSettings: <AdminPanelSettings />,
    Settings: <Settings />,
    Security: <Security />,
    Domain: <Domain />,
    Store: <Store />,
    Lock: <Lock />,
    Badge: <Badge />,
    Person: <Person />,
    Tune: <Tune />,
    LibraryBooks: <LibraryBooks />,
    Extension: <Extension />,
    ViewQuilt: <ViewQuilt />,
    LocalGasStation: <LocalGasStation />,
    CreditCard: <CreditCard />,
    Speed: <Speed />,
    TrendingUp: <TrendingUp />,
    PieChart: <PieChart />,
    Message: <Message />,
    CloudUpload: <CloudUpload />,
    Quiz: <Quiz />,
    Help: <Help />,
    Assessment: <Assessment />,
    Link: <Link />,
    ListAlt: <ListAlt />,
    LocalOffer: <LocalOffer />,
    DashboardCustomize: <DashboardCustomize />,
    Traffic: <Traffic />,
    Drafts: <Drafts />,
    Email: <Email />,
    // boubaker 23/08/2026 - menu Impressions Academy
    Description: <DescriptionIcon />,
    AccountBalance: <AccountBalanceIcon />,
    Dashboard: <Dashboard />,
    Assignment: <Assignment />,
    Explore: <Explore />,
    SupportAgent: <SupportAgent />,
    GridView: <GridView />,
    FactCheck: <FactCheck />,
    School: <School />,
    Groups: <Groups />,
    ContactPage: <ContactPage />,
    TransferWithinAStation: <TransferWithinAStation />,
    Verified: <Verified />,
    Mail: <Mail />,
    LocalHospital: <LocalHospital />,
    TableChart: <TableChart />,
    CardMembership: <CardMembership />,
    WorkspacePremium: <WorkspacePremium />,
    AutoAwesome: <AutoAwesome />,
    DeleteSweep: <DeleteSweep />
};

// Default mapping for items based on translationKey or caption (Fallback for corrupted DB data)
export const defaultIconMapping: { [key: string]: string } = {
    'academy.menu.subscription': 'CardMembership',
    'academy.menu.planning_theorique_crud': 'CalendarMonth',
    'academy.menu.planning_theorique_matrix': 'CalendarMonth',
    'academy.menu.planning_generation': 'AutoAwesome',
    'academy.menu.planning_deletion': 'DeleteSweep',
    // Top Level Headers
    'menu.planning.commande': 'ShoppingCart',
    'menu.planning.planning': 'CalendarMonth',
    'menu.planning.facturation': 'Receipt',
    'menu.planning.editions': 'Print',
    'menu.planning.entite': 'Business',
    'menu.planning.pilotage': 'Business', // duplicate?
    'menu.planning.administration': 'AdminPanelSettings',

    // Maker
    'menu.maker.libraries': 'LibraryBooks',
    'menu.maker.components': 'Extension',
    'menu.maker.templates': 'ViewQuilt',

    // Sub items
    'menu.planning.admin.configMenu': 'Tune',
    'menu.planning.admin.securityWeb': 'Security',
    'menu.planning.admin.accessRights.securityWeb': 'Security',
    'menu.planning.admin.societe': 'Domain',
    'menu.planning.admin.societe.agence': 'Store',
    'menu.planning.admin.accessRights': 'Lock',
    'menu.planning.admin.accessRights.profiles': 'Badge',
    'menu.planning.admin.accessRights.users': 'Person',
    'menu.planning.admin.accessRights.sysParams': 'Tune',
    'menu.planning.admin.parameters': 'Settings',

    // Parc
    'menu.parc.fuel': 'LocalGasStation',
    'menu.parc.fuelcards': 'CreditCard',
    'menu.parc.mileage': 'Speed',
    'menu.parc.analysis.general': 'TrendingUp',
    'menu.parc.analysis.tco': 'PieChart',
    'menu.parc.alerts': 'Message',
    'menu.parc.lookups': 'Tune',

    // Fallbacks by Caption (if translationKey missing)
    'Commande': 'ShoppingCart',
    'Planning': 'CalendarMonth',
    'Facturation': 'Receipt',
    'Editions': 'Print',
    'Entite': 'Business',
    'Administration': 'AdminPanelSettings',
};

// Returns the ReactNode for a given icon name
export const getIcon = (iconName?: string): React.ReactNode | undefined => {
    if (!iconName) return undefined;
    return iconMap[iconName];
};

// Smart resolver that tries to find an icon for a menu item
export const resolveIcon = (item: MenuItem): React.ReactNode | undefined => {
    // 1. Explicit clean string from DB/Item
    if (typeof item.icon === 'string' && iconMap[item.icon]) {
        return iconMap[item.icon];
    }

    // 2. Fallback: Lookup by translationKey
    if (item.translationKey && defaultIconMapping[item.translationKey]) {
        const iconName = defaultIconMapping[item.translationKey];
        if (iconMap[iconName]) return iconMap[iconName];
    }

    // 3. Fallback: Lookup by Caption
    if (item.caption && defaultIconMapping[item.caption]) {
        const iconName = defaultIconMapping[item.caption];
        if (iconMap[iconName]) return iconMap[iconName];
    }

    return undefined;
};

export const getMenuForApp = (appName: string | null): MenuItem[] => [];

export const menuItems: MenuItem[] = [];
